import * as TFF from '@tff/types/tff';
import { IntlShape } from 'react-intl';
import { SsrsByBond, SsrsByPax } from '../../../../types/ancillaryTypes';
import { RowSsrObjectType, RowsType } from '../../../../types/ancillaryCostTableRowTypes';
import { MetaValue } from '@tff/types/tff/tfa-amend-ancillary-service';

type CommonParamsType = {
  intl: IntlShape;
  bookedSsrs: SsrsByPax;
  outboundFlightOnd: string;
  originalAncillaries: TFF.Ancillaries;
  rebooking: boolean | undefined;
};

/** for each new ssr, adds a new object in the rows variable */
export const populateRowsWithData = (
  data: SsrsByBond,
  rows: RowsType,
  outboundMessage: string,
  inboundMessage: string,
  outboundMetaValues: MetaValue[],
  inboundMetaValues: MetaValue[],
  bookedSsrs: SsrsByPax,
  outboundFlightOnd: string,
  intl: IntlShape,
  originalAncillaries: TFF.Ancillaries,
  rebooking: boolean | undefined,
) => {
  const outboundIndex = (rows as RowSsrObjectType[]).findIndex(item => item.label === outboundMessage) + 1;
  const inboundIndex = (rows as RowSsrObjectType[]).findIndex(item => item.label === inboundMessage) + 1;

  const commonParams: CommonParamsType = {
    intl,
    bookedSsrs,
    outboundFlightOnd,
    originalAncillaries,
    rebooking,
  };

  addNewObjectByOndToRows(data, 'outbound', outboundIndex, outboundMetaValues, rows, commonParams);
  addNewObjectByOndToRows(data, 'return', inboundIndex, inboundMetaValues, rows, commonParams);
};

const addNewObjectByOndToRows = (
  data: SsrsByBond,
  bond: 'outbound' | 'return',
  bondIndex: number,
  metaValues: MetaValue[],
  rows: RowsType,
  commonParams: CommonParamsType,
) => {
  const { intl, bookedSsrs, outboundFlightOnd, originalAncillaries, rebooking } = commonParams;
  const { outboundAmount, inboundAmount } = getOriginalFlightSsrsData(bookedSsrs, outboundFlightOnd);
  data[bond].forEach((flight, index: number) => {
    const ssrType = findParentByCode(metaValues, flight.code);

    const codeWithoutRemove = flight.code.replace('-remove', '').trim();
    const originalSsrAmount =
      bond === 'outbound' ? outboundAmount[codeWithoutRemove] ?? 0 : inboundAmount[codeWithoutRemove] ?? 0;
    const isNewSsrPresent: boolean = findParentByCode(metaValues, codeWithoutRemove) !== undefined;

    const isSsrToBeRemoved = flight.code.includes('-remove');
    const newSsrAmount = isSsrToBeRemoved ? originalSsrAmount - (flight.amount ?? 0) : flight.amount ?? 0;
    const totalSsrAmount = rebooking
      ? newSsrAmount
      : calculateTotalSsrAmount(flight.code, flight.amount, originalSsrAmount);
    const displayAmount = isSsrToBeRemoved ? newSsrAmount : totalSsrAmount;
    const newSsrAmountAndDescription = getNewSsrAmountAndDescription(flight.code, isNewSsrPresent, intl, displayAmount);
    const originalTotalPrice =
      originalSsrAmount !== 0
        ? originalSsrAmount * findOriginalPrice(codeWithoutRemove, originalAncillaries, metaValues)
        : 0;
    const newSsrsPrice = isNewSsrPresent ? newSsrAmount * findPriceByCode(metaValues, codeWithoutRemove) : 0;
    const newTotalPrice = isSsrToBeRemoved
      ? newSsrsPrice
      : calculateNewPrice(rebooking, originalTotalPrice, newSsrsPrice);
    const surcharge = newTotalPrice - originalTotalPrice;

    const formattedFlight: RowSsrObjectType = {
      label: ssrType ? ssrType.type : '',
      original: `${originalSsrAmount} x ${intl.formatMessage({ id: `ssr.${codeWithoutRemove}.shortDescription` })}`,
      new: newSsrAmountAndDescription,
      surcharge: surcharge,
      ond: flight.segmentOnd,
      code: `${flight.segmentOnd}-${flight.code}`,
      originalPrice: originalTotalPrice,
      newPrice: newTotalPrice,
    };
    const indexToInsertObject = bond === 'outbound' ? bondIndex + index : data.outbound.length + bondIndex + index;
    rows.splice(indexToInsertObject, 0, formattedFlight);
  });
};

const getOriginalFlightSsrsData = (bookedSsrs: SsrsByPax, outboundFlightOnd: string) => {
  let outboundAmount = {};
  let inboundAmount = {};
  Object.values(bookedSsrs).forEach((pax: any) => {
    pax.forEach(({ code, segmentOnd, amount }) => {
      if (code !== 'BA0') {
        if (segmentOnd === outboundFlightOnd) {
          if (code.includes('BX')) {
            outboundAmount[code] = (outboundAmount[code] || 0) + amount;
          } else {
            outboundAmount[code] = (outboundAmount[code] || 0) + 1;
          }
        } else {
          if (code.includes('BX')) {
            inboundAmount[code] = (inboundAmount[code] || 0) + amount;
          } else {
            inboundAmount[code] = (inboundAmount[code] || 0) + 1;
          }
        }
      }
    });
  });

  return { outboundAmount, inboundAmount };
};

const getNewSsrAmountAndDescription = (
  code: string,
  isNewSsrPresent: boolean,
  intl: IntlShape,
  newSsrAmount: number,
) => {
  return isNewSsrPresent
    ? `${newSsrAmount} x ${intl.formatMessage({
        id: `ssr.${code.replace('-remove', '').trim()}.shortDescription`,
      })}`
    : 'This Ssr is Not Available For This Flight!';
};

export const findOriginalPrice = (code: string, originalAncillaries: TFF.Ancillaries, metaValues: any[]) => {
  if (Object.values(originalAncillaries).length > 0) {
    for (const key in originalAncillaries) {
      if (key.includes(code)) {
        const amount = originalAncillaries[key].TotalAmount;
        return amount && !isNaN(amount) ? amount : 0;
      }
    }
  }
  return findPriceByCode(metaValues, code) ?? 0;
};

const findPriceByCode = (metaValues: MetaValue[], code: string): number | undefined => {
  const ancillary = metaValues.find(anc => anc.code === code);

  return ancillary?.price.amount;
};

const findParentByCode = (data: MetaValue[], code: string) =>
  data.find(item => item.code === code.replace('-remove', ''));

export const calculateTotalSsrAmount = (code: string, amount: number, originalSsrAmount: number) => {
  if (code.includes('BX')) {
    return amount ?? 0;
  }
  return (amount ?? 0) + originalSsrAmount;
};

export const calculateNewPrice = (rebooking: boolean | undefined, originalTotalPrice: number, newSsrsPrice: number) => {
  if (rebooking) {
    return newSsrsPrice;
  } else {
    return originalTotalPrice + newSsrsPrice;
  }
};
